import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BaseModal, BaseModalContent } from 'src/shared/components/ui/modals/BaseModal';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { usePendingInvites, useMutateInvites, invitesResponseType } from 'src/shared/queries/usePendingInvites';

export default function PendingInvites() {
  const { switchOrg } = useOrg();
  const queryClient = useQueryClient();
  const { isLoading, data } = usePendingInvites();
  const { acceptInvite, denyInvite } = useMutateInvites();
  const [open, setOpen] = useState(true);
  const [openAccepted, setOpenAccepted] = useState(false);

  if (!openAccepted && (isLoading || !data || data.length < 1 || !data?.[0]?.id)) return <></>;

  const invite: invitesResponseType = data?.[0]!;

  async function handleReplyToInvite(id: string, type: 'accept' | 'deny') {
    try {
      if (type === 'accept') {
        await acceptInvite.mutateAsync(id);
      } else {
        await denyInvite.mutateAsync(id);
      }

      setOpen(false);
      if (type === 'accept') {
        setOpenAccepted(true);
      }
    } catch {
      // Error handling is already done in the mutations
    }
  }

  return (
    <>
      <BaseModal open={open} onOpenChange={setOpen}>
        <BaseModalContent
          title="New invite!"
          subtleTitle="Invite received"
          description={`${invite?.sender.email || 'Someone'} invited you to join ${invite?.organisation.name || 'their organisation'}`}
          cancelText="Decline invite"
          confirmText="Accept invite"
          submitLoading={acceptInvite.isPending || denyInvite.isPending}
          onConfirm={() => handleReplyToInvite(invite.id, 'accept')}
          onCancel={() => handleReplyToInvite(invite.id, 'deny')}
        />
      </BaseModal>
      <BaseModal open={openAccepted} onOpenChange={(open) => setOpenAccepted(open)}>
        <BaseModalContent
          title="Invite has been accepted!"
          subtleTitle="Invite accepted"
          description={`You are now a member of ${invite?.organisation.name || 'their organisation'}. You can now switch between your current organisation(s) the new one. `}
          cancelText="Close"
          onCancel={() => {
            queryClient.invalidateQueries({ queryKey: ['orgs'] });
            setOpenAccepted(false);
          }}
          onConfirm={() => {
            switchOrg(invite?.organisation.id!);
            setOpenAccepted(false);
          }}
          confirmText={`Enter ${invite?.organisation.name || 'new organisation'}`}
        />
      </BaseModal>
    </>
  );
}
